import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container } from '../components/layout/Primitives';
import { USE_API } from '../config/env';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchReviewsOverviewThunk, selectReviewsOverview } from '../store/actions/reviewActions';
import StarRating from '../components/reviews/StarRating';

const Hero = styled.section`
  background: hsla(38, 25%, 90%, 0.5);
  border-bottom: 1px solid hsl(35, 18%, 84%);
  padding: clamp(3rem, 8vw, 5rem) 1.5rem;
  text-align: center;
`;

const SummaryRow = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  font-size: ${({ theme }) => theme.type.bodySm};
  opacity: 0.75;
`;

const Grid = styled.div`
  display: grid;
  gap: clamp(1.75rem, 3vw, 2.25rem);
  grid-template-columns: 1fr;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Card = styled.article`
  border: 1px solid hsl(35, 18%, 84%);
  padding: clamp(1.25rem, 2vw, 1.75rem);
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
`;

const ThumbLink = styled(Link)`
  display: block;
  aspect-ratio: 1;
  background: hsl(38, 12%, 90%);
  overflow: hidden;
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export default function ReviewsPage() {
  const dispatch = useAppDispatch();
  const ov = useAppSelector(selectReviewsOverview);

  useEffect(() => {
    if (!USE_API) return;
    dispatch(fetchReviewsOverviewThunk(24));
  }, [dispatch]);

  const list = USE_API ? ov?.reviews ?? [] : [];
  const summary = ov?.summary ?? { average: 0, count: 0 };

  return (
    <div className="animate-fade-in">
      <Hero>
        <p className="font-mono-luxe" style={{ margin: '0 0 1rem', fontSize: '11px', color: '#524D46' }}>
          Client voices
        </p>
        <h1 className="font-display" style={{ margin: 0, fontSize: 'clamp(2rem, 3vw + 1rem, 3rem)', fontWeight: 300 }}>
          Reviews
        </h1>
        {USE_API && ov?.loading ? (
          <p style={{ marginTop: '1rem', opacity: 0.65 }}>Loading storefront reviews…</p>
        ) : null}
        {USE_API && ov?.error ? (
          <p role="alert" style={{ marginTop: '1rem', color: '#8B3A2A' }}>
            {typeof ov.error === 'string' ? ov.error : 'Could not load reviews.'}
          </p>
        ) : null}
        {USE_API && !ov?.loading && summary.count > 0 ? (
          <SummaryRow>
            <span>{summary.count} approved reviews</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              Store average{' '}
              <strong>{summary.average.toFixed(1)}</strong>{' '}
              <StarRating rating={Number(summary.average)} size="sm" />
            </span>
          </SummaryRow>
        ) : null}
      </Hero>

      <Container style={{ padding: 'clamp(3rem, 6vw, 5rem) 1.5rem', maxWidth: '56rem' }}>
        {!USE_API ? (
          <>
            <p style={{ textAlign: 'center', lineHeight: 1.7, opacity: 0.75 }}>
              Individual product pages load verified reviews while the storefront API is enabled.
            </p>
            <p style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Link to="/shop/all" style={{ textDecoration: 'underline' }}>
                Browse the catalogue
              </Link>
            </p>
          </>
        ) : list.length === 0 && !ov?.loading && !ov?.error ? (
          <p style={{ textAlign: 'center', opacity: 0.7 }}>
            No approved reviews published yet — open a piece and leave the first rating when you sign in.
          </p>
        ) : (
          <Grid>
            {list.map((r) => {
              const p = r.product;
              const slug = typeof p?.id === 'string' && p.id ? p.id : '';
              const to = slug ? `/product/${encodeURIComponent(slug)}` : '/shop/all';
              return (
                <Card key={r.id}>
                  <ThumbLink to={to} aria-hidden={!p?.image}>
                    {p?.image ? <ThumbImg src={p.image} alt="" loading="lazy" /> : null}
                  </ThumbLink>
                  <div>
                    <Link
                      to={to}
                      style={{ fontWeight: 500, fontSize: '0.9375rem', color: 'inherit', textDecoration: 'none' }}
                    >
                      {p?.name || 'Product'}
                    </Link>
                    <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <StarRating rating={Number(r.rating)} size="sm" />
                      <span style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.55 }}>
                        {r.author || 'Reviewer'}
                      </span>
                    </div>
                    {r.title ? <p style={{ margin: '0.75rem 0 0', fontWeight: 500 }}>{r.title}</p> : null}
                    {r.body ? (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', lineHeight: 1.55, opacity: 0.82 }}>
                        {r.body}
                      </p>
                    ) : null}
                  </div>
                </Card>
              );
            })}
          </Grid>
        )}
      </Container>
    </div>
  );
}
